/* eslint-disable  @typescript-eslint/no-explicit-any */ // AJB: remove
"use client";

import { PRIMARY_COLOR } from "../../layout/LayoutConstants";
import { Box, Typography } from "@mui/material";
import Image from "next/image";
import PillButton from "@/app/shared/PillButton";
import { AdVertical } from "@/sell/models/AdVerticals";
import { useLazySession } from "@/app/layout/useLazySession";
import { CheckoutService } from "@/services/checkout/CheckoutService";
import { AD_DETAILS_DEFAULTS } from "@/sell/models/AdDetails";
import { AD_CONFIG_STATE_DEFAULTS } from "@/sell/models/AdConfigState";
import Link from "next/link";

interface SellPrimaryCategoryProps {
  title: string;
  subtitle: string[];
  icon: string;
  vertical: AdVertical;
  freeAdsBelow?: string;
  minimumAdCost?: string;
}

function SellPrimaryCategory({
  title,
  subtitle,
  icon,
  vertical,
  freeAdsBelow,
  minimumAdCost,
}: SellPrimaryCategoryProps) {
  const { session } = useLazySession();
  const hasDynamicPricing = !!CheckoutService.GetAdDynamicPrices({
    config: AD_CONFIG_STATE_DEFAULTS,
    details: AD_DETAILS_DEFAULTS,
    vertical,
  });
  const requiresPricingSelection =
    hasDynamicPricing &&
    (!session?.user || (session.user as any).adPricingModel !== "free_basic");
  return (
    <Box
      sx={{
        background: "white",
        borderRadius: 10,
        boxShadow: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: 2,
        gap: 2,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: 100,
          img: {
            objectFit: "contain",
          },
        }}
      >
        <Image
          src={`/icons/categories/${icon}.png`}
          alt={title}
          style={{ borderRadius: 10 }}
          unoptimized={true}
          fill
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 1,
        }}
      >
        <Typography variant="h5" component="h2" sx={{ fontWeight: "bold" }}>
          {title}
        </Typography>
        <Box>
          {subtitle.map((line, index) => (
            <Typography key={index}>{line}</Typography>
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Link
          href={
            requiresPricingSelection
              ? `/sell/create/${vertical}`
              : `/sell/ad/create/${btoa(JSON.stringify({ vertical }))}`
          }
          prefetch={false}
        >
          <PillButton
            primary={true}
            fullWidth
            sx={{
              "&&&": {
                borderColor: PRIMARY_COLOR,
                color: PRIMARY_COLOR,
                fontWeight: "bold",
              },
            }}
          >
            Start Selling
          </PillButton>
        </Link>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" sx={{ textAlign: "center" }}>
            {!hasDynamicPricing && (
              <>
                from {minimumAdCost} until sold
                {freeAdsBelow && (
                  <>
                    <br />
                    or{" "}
                  </>
                )}
              </>
            )}
            {freeAdsBelow && <>free under {freeAdsBelow}</>}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export const SELL_LANDING_CATEGORIES: Array<SellPrimaryCategoryProps> = [
  {
    title: "Horses",
    subtitle: ["for sale, lease, wanted & ", "stallions at stud"],
    icon: "horse",
    vertical: "horse",
    freeAdsBelow: "$5k",
  },
  {
    title: "Floats & Trucks",
    subtitle: ["including trailers, tow vehicles & motorhomes"],
    icon: "trailer",
    vertical: "vehicle",
    freeAdsBelow: "$10k",
  },
  {
    title: "Saddlery, Tack & More",
    subtitle: ["including equipment, apparel, ", "products & more"],
    icon: "saddle",
    vertical: "saddlery-and-more",
    freeAdsBelow: "$3k",
    minimumAdCost: "$29",
  },
  {
    title: "Property, Services +",
    subtitle: ["anything else equine related for sale, lease or wanted"],
    icon: "property",
    vertical: "property",
    minimumAdCost: "$99",
  },
];

export default function SellLandingCategories() {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <Typography
        variant="h3"
        component="h1"
        sx={{ textAlign: "center", color: "white" }}
      >
        What are you selling today?
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
          },
          gap: 6,
          padding: 2,
        }}
      >
        {SELL_LANDING_CATEGORIES.map((category) => (
          <SellPrimaryCategory key={category.vertical} {...category} />
        ))}
      </Box>
    </Box>
  );
}
